import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import {
  ArrowLeft,
  Sparkles,
  CheckCircle,
  AlertTriangle,
  Clock,
  Users as UsersIcon,
} from "lucide-react"
import {
  getAiGenerationsBreakdown,
  type AiGenerationsBreakdown,
} from "@/lib/api"
import { StaleBanner } from "@/components/StaleBanner"

const RANGES = [7, 30, 90] as const

const COLORS = { generated: "#10b981", extended: "#3b82f6", overwritten: "#f59e0b" }

const tooltipStyle = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  fontSize: 12,
}

function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return "—"
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`
}

function Stat({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm font-medium mt-0.5">{label}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  )
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="text-sm font-semibold">{title}</h3>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5 mb-3">{subtitle}</p>}
      <div className={subtitle ? "" : "mt-3"}>{children}</div>
    </div>
  )
}

export function AiGenerationsPage() {
  const navigate = useNavigate()
  const [days, setDays] = useState<number>(30)
  const [data, setData] = useState<AiGenerationsBreakdown | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getAiGenerationsBreakdown(days)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load") })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [days])

  const byType = data?.by_type
  const totalGenerations =
    (byType?.generated ?? 0) + (byType?.extended ?? 0) + (byType?.overwritten ?? 0)

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> Dashboard
        </button>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="size-6 text-primary" /> AI Generations
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every season the AI builds, extends, or overwrites. All figures are computed by PostHog
            and exclude internal/test users.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border p-1">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              className={`px-3 py-1 text-sm rounded-md ${
                days === r ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-28 rounded-lg border bg-muted animate-pulse" />)}
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center text-sm text-destructive">
          {error}
        </div>
      )}

      {!loading && data && !data.configured && (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">
            This drill-down reads from PostHog. Set <code>POSTHOG_PERSONAL_API_KEY</code> +{" "}
            <code>POSTHOG_PROJECT_ID</code> on the backend to populate it.
          </p>
        </div>
      )}

      {!loading && data?.configured && data.error && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          Couldn't reach PostHog for this query. Try again shortly.
        </div>
      )}

      {!loading && data?.configured && !data.error && (
        <>
          <StaleBanner asOf={data.stale_as_of} />
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Type composition · last {data.window_days}d
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat label="Total" value={totalGenerations} sub="all pipeline events" />
              <Stat label="New generations" value={byType?.generated ?? 0} sub="season_generated" />
              <Stat label="Extensions" value={byType?.extended ?? 0} sub="season_extended" />
              <Stat label="Overwrites" value={byType?.overwritten ?? 0} sub="season_overwritten" />
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Reliability" subtitle="Did the pipeline succeed?">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-bold flex items-center gap-1">
                    <CheckCircle className="size-5 text-emerald-500" />
                    {data.reliability?.success_rate_pct != null ? `${data.reliability.success_rate_pct}%` : "—"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Success rate ({data.reliability?.succeeded ?? 0}/
                    {(data.reliability?.succeeded ?? 0) + (data.reliability?.failed ?? 0)})
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold flex items-center gap-1">
                    <AlertTriangle className="size-5 text-amber-500" />
                    {data.reliability?.failed ?? 0}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Failures · {data.reliability?.fallback_used ?? 0} fallbacks used
                  </div>
                </div>
              </div>
            </Card>
            <Card title="Compute time" subtitle="How long the AI takes to build a plan">
              <div className="grid grid-cols-3 gap-3">
                <Stat label="P50" value={fmtMs(data.compute_ms?.p50)} />
                <Stat label="P90" value={fmtMs(data.compute_ms?.p90)} />
                <Stat label="P99" value={fmtMs(data.compute_ms?.p99)} />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                avg {fmtMs(data.compute_ms?.avg)} · n={data.compute_ms?.sample_size ?? 0} (mc3_generation_completed)
              </p>
            </Card>
          </section>

          <Card title="Daily trend" subtitle={`Generations / extensions / overwrites per day · last ${data.window_days}d`}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.daily ?? []} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))"
                  tickFormatter={(d: string) => d?.slice(5)} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="generated" stackId="a" fill={COLORS.generated} name="Generated" />
                <Bar dataKey="extended" stackId="a" fill={COLORS.extended} name="Extended" />
                <Bar dataKey="overwritten" stackId="a" fill={COLORS.overwritten} name="Overwritten" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Extensions by trigger" subtitle="Where extensions come from (e.g. weekly cron vs user)">
              {(data.by_source?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">No extensions in this window.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.by_source!.map((s) => (
                    <div key={s.source} className="flex items-center justify-between text-sm">
                      <span className="font-mono text-xs">{s.source}</span>
                      <span className="text-muted-foreground tabular-nums">{s.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            <Card title="Top generators" subtitle="Users by new-season generations">
              {(data.by_user?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <UsersIcon className="size-4" /> No generations in this window.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {data.by_user!.map((u, i) => (
                    <div key={u.user + i} className="flex items-center justify-between text-sm">
                      <span className="truncate"><span className="text-muted-foreground mr-2">{i + 1}.</span>{u.user}</span>
                      <span className="text-muted-foreground tabular-nums">{u.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </section>

          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="size-3" /> Source of truth: PostHog. Internal/test users excluded.
          </p>
        </>
      )}
    </div>
  )
}
